import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { ReactComponent as XTwitterIcon } from 'src/images/svgs/socials/x-twitter.svg';
import { ReactComponent as FacebookIcon } from 'src/images/svgs/socials/facebook.svg';
import { ReactComponent as LinkedinIcon } from 'src/images/svgs/socials/linkedin.svg';
import { StyledSocialButton } from 'src/common/styles';
import { StyledLoading } from 'src/common/components';
import { SegmentShareEvent } from 'src/common/types';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useGenerateBadgeImageMutation } from 'src/features/public-profile/public-profile.slice';
import { Achievement } from 'src/common/page-share';
import { StyledContentWrapper, StyledSubTitle, StyledTitle } from '../../congratulations-full-screen.styles';
import { StyledBadgeContent, StyledBadgeTitle } from '../../../awards-badge-popup/awards-badge-popup.styles';
import { StyledButtonLink, StyledGenerateButton, StyledSocialButtonsWrapper, StyledSocialWrapper } from './sharing-badge-step.styles';
import { ShareBadgeProps } from './sharing-badge-step.types';
export const SharingBadgeStep = ({
  awardedBadge,
  roomCode,
  isLoading
}: ShareBadgeProps) => {
  const [trackEvent] = useTrackEventMutation();
  const {
    data: userData
  } = useGetUserQuery();
  const userName = userData?.data?.user?.username ?? '';
  const [generateBadgeImage, {
    isLoading: isGeneratingBadgeImage
  }] = useGenerateBadgeImageMutation();
  const [isShareBadgeDisabled, setIsShareBadgeDisabled] = useState(true);
  const badgeUrl = `https://tryhackme.com/${userName ?? ''}/badges/${awardedBadge?.name ?? ''}`;
  const trackShareClick = useCallback((event: SegmentShareEvent) => {
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'social-share',
      properties: {
        page: 'congratulations-page',
        achievement: Achievement.badge,
        name: awardedBadge?.name,
        medium: event.split('-').slice(1).join('-'),
        'page-referrer': roomCode
      }
    });
  }, [trackEvent, roomCode, awardedBadge?.name]);
  const handleGenerateBadgeImage = async (badgeName: string): Promise<void> => {
    try {
      await generateBadgeImage({
        badgeName
      }).unwrap();
      setIsShareBadgeDisabled(false);
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Something went wrong.');
    }
  };
  const handleGenerateClick = () => {
    if (awardedBadge?.name) {
      // eslint-disable-next-line no-void
      void handleGenerateBadgeImage(awardedBadge.name);
    }
  };
  return <StyledContentWrapper data-content="badge-sharing" data-sentry-element="StyledContentWrapper" data-sentry-component="SharingBadgeStep" data-sentry-source-file="sharing-badge-step.tsx">
      <header>
        <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="sharing-badge-step.tsx">Badge Earned!</StyledTitle>
        <StyledSubTitle data-sentry-element="StyledSubTitle" data-sentry-source-file="sharing-badge-step.tsx">
          Your dedication just paid off! You&apos;ve earned the {awardedBadge?.title} badge. Keep pushing forward and
          collect even more achievements as you progress. The next challenge awaits!
        </StyledSubTitle>
      </header>
      <StyledBadgeContent data-modal="congratulations" data-sentry-element="StyledBadgeContent" data-sentry-source-file="sharing-badge-step.tsx">
        {isLoading ? <>
            <StyledLoading width="255px" height="300px" data-testid="loading-image" />
            <StyledLoading width="180px" height="50px" />
          </> : <>
            <Image src={`https://assets.tryhackme.com${awardedBadge?.image ?? ''}`} width="255" height="300" />
            <div>
              <StyledBadgeTitle data-modal="congratulations">{awardedBadge?.title}</StyledBadgeTitle>
              <p data-modal="congratulations">{awardedBadge?.description}</p>
            </div>
          </>}
      </StyledBadgeContent>
      <StyledSocialWrapper data-sentry-element="StyledSocialWrapper" data-sentry-source-file="sharing-badge-step.tsx">
        <p>Share your badge:</p>
        {!!isShareBadgeDisabled && <StyledGenerateButton isLoading={isGeneratingBadgeImage} isDisabled={!isShareBadgeDisabled} loadingMsg="Generating..." onClick={handleGenerateClick}>
            Generate
          </StyledGenerateButton>}

        <StyledSocialButtonsWrapper data-sentry-element="StyledSocialButtonsWrapper" data-sentry-source-file="sharing-badge-step.tsx">
          <StyledSocialButton data-social="linkedin" target="_blank" href={`https://www.linkedin.com/sharing/share-offsite/?url=${badgeUrl}`} onClick={() => trackShareClick('share-linkedin')} $isDisabled={isShareBadgeDisabled} data-sentry-element="StyledSocialButton" data-sentry-source-file="sharing-badge-step.tsx">
            <LinkedinIcon data-sentry-element="LinkedinIcon" data-sentry-source-file="sharing-badge-step.tsx" />
            Linkedin
          </StyledSocialButton>
          <StyledSocialButton data-social="twitter-x" target="_blank" href={`https://twitter.com/intent/tweet?url=${badgeUrl}&via=realtryhackme&text=${awardedBadge?.description ?? ''}&hashtags=tryhackme`} onClick={() => trackShareClick('share-twitter')} $isDisabled={isShareBadgeDisabled} data-sentry-element="StyledSocialButton" data-sentry-source-file="sharing-badge-step.tsx">
            <XTwitterIcon data-sentry-element="XTwitterIcon" data-sentry-source-file="sharing-badge-step.tsx" />
            Twitter / X
          </StyledSocialButton>
          <StyledSocialButton data-social="facebook" target="_blank" href={`https://www.facebook.com/sharer/sharer.php?u=${badgeUrl}&t=${awardedBadge?.description ?? ''}`} onClick={() => trackShareClick('share-facebook')} $isDisabled={isShareBadgeDisabled} data-sentry-element="StyledSocialButton" data-sentry-source-file="sharing-badge-step.tsx">
            <FacebookIcon data-sentry-element="FacebookIcon" data-sentry-source-file="sharing-badge-step.tsx" />
            Facebook
          </StyledSocialButton>
          <StyledButtonLink as={Link} to={`/${userName}/badges/${awardedBadge?.name ?? ''}`} variant="outlined" color="secondary" data-btn="link" target="_blank" $isDisabled={isShareBadgeDisabled} data-sentry-element="StyledButtonLink" data-sentry-source-file="sharing-badge-step.tsx">
            <FontAwesomeIcon icon={faLink} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="sharing-badge-step.tsx" />
            Go to link
          </StyledButtonLink>
        </StyledSocialButtonsWrapper>
      </StyledSocialWrapper>
    </StyledContentWrapper>;
};